/**
 * OnboardingTutorial Component
 * 
 * İlk kez giriş yapan kullanıcılara sitenin kullanımını anlatan
 * basit ve atlanabilir bir tutorial.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

// Tutorial adimlari — metin i18n key'lerinden render-aninda cozulur (t(step.titleKey))
const TUTORIAL_STEPS = [
  { id: 'welcome', titleKey: 'tutorial.welcome_title', descKey: 'tutorial.welcome_desc', highlight: null, position: 'center' },
  { id: 'nav-tabs', titleKey: 'tutorial.nav_title', descKey: 'tutorial.nav_desc', highlight: '[data-testid="nav-tabs"]', position: 'bottom' },
  { id: 'username-input', titleKey: 'tutorial.username_title', descKey: 'tutorial.username_desc', highlight: '[data-testid="username-input"]', position: 'bottom' },
  { id: 'payment-input', titleKey: 'tutorial.payment_title', descKey: 'tutorial.payment_desc', highlight: '[data-testid="payment-input"]', position: 'bottom' },
  { id: 'analyze-button', titleKey: 'tutorial.analyze_title', descKey: 'tutorial.analyze_desc', highlight: '[data-testid="analyze-button"]', position: 'top' },
  { id: 'credits', titleKey: 'tutorial.credits_title', descKey: 'tutorial.credits_desc', highlight: null, position: 'center' }
];

const STORAGE_KEY = 'opara_tutorial_completed';

const OnboardingTutorial = ({ isNewUser = false }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);

  // Tutorial'ı gösterip göstermeme kontrolü
  useEffect(() => {
    const tutorialCompleted = localStorage.getItem(STORAGE_KEY);
    
    // Sadece yeni kullanıcılara ve daha önce tamamlamamışlara göster
    if (isNewUser && !tutorialCompleted) {
      // Kısa bir gecikme ile göster (sayfa yüklendikten sonra)
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isNewUser]);

  // Highlight edilen elementi bul ve pozisyonunu al
  useEffect(() => {
    if (!isVisible) return;
    
    const step = TUTORIAL_STEPS[currentStep];
    if (step.highlight) {
      // requestAnimationFrame ile state güncellemesini geciktir
      const updateHighlight = () => {
        const element = document.querySelector(step.highlight);
        if (element) {
          const rect = element.getBoundingClientRect();
          setHighlightRect({
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16
          });
          
          // Elementi görünür alana kaydır
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          setHighlightRect(null);
        }
      };
      
      requestAnimationFrame(updateHighlight);
    } else {
      setHighlightRect(null);
    }
  }, [currentStep, isVisible]);

  // Tutorial'ı kapat ve kaydet
  const closeTutorial = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsVisible(false);
  };

  // Sonraki adım
  const nextStep = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      closeTutorial();
    }
  };

  // Önceki adım
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isVisible) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  // Tooltip pozisyonunu hesapla
  const getTooltipStyle = () => {
    if (step.position === 'center' || !highlightRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
    }

    const style = {
      position: 'fixed',
      left: Math.max(20, Math.min(highlightRect.left, window.innerWidth - 420))
    };

    if (step.position === 'bottom') {
      style.top = highlightRect.top + highlightRect.height + 16;
    } else if (step.position === 'top') {
      style.top = highlightRect.top - 200;
    }

    return style;
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-[9998] bg-black/70 transition-opacity duration-300"
        onClick={closeTutorial}
      />

      {/* Highlight Box */}
      {highlightRect && (
        <div
          className="fixed z-[9999] border-2 border-[#FFDD59] rounded-lg pointer-events-none transition-all duration-300"
          style={{
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7), 0 0 20px rgba(255, 221, 89, 0.5)'
          }}
        />
      )}

      {/* Tooltip Card */}
      <div
        className="fixed z-[10000] w-[400px] bg-gray-900 border-2 border-[#FFDD59] rounded-2xl shadow-2xl overflow-hidden"
        style={getTooltipStyle()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FFDD59]/20 to-orange-500/20 px-5 py-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FFDD59]" />
              <span className="text-sm text-gray-400">
                {t('tutorial.step_counter', { current: currentStep + 1, total: TUTORIAL_STEPS.length })}
              </span>
            </div>
            <button 
              onClick={closeTutorial}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label={t('common.close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <h3 className="text-lg font-bold text-white mt-2">
            {t(step.titleKey)}
          </h3>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">
            {t(step.descKey)}
          </p>
        </div>

        {/* Footer - Navigation */}
        <div className="px-5 py-4 bg-gray-800/50 border-t border-gray-700 flex items-center justify-between">
          {/* Skip Button */}
          <button
            onClick={closeTutorial}
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            {t('tutorial.skip')}
          </button>

          {/* Navigation Buttons */}
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <Button
                onClick={prevStep}
                variant="outline"
                size="sm"
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('tutorial.back')}
              </Button>
            )}
            <Button
              onClick={nextStep}
              size="sm"
              className="bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-medium"
            >
              {isLastStep ? (
                t('tutorial.start')
              ) : (
                <>
                  {t('tutorial.next')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="h-1 bg-gray-800">
          <div 
            className="h-full bg-[#FFDD59] transition-all duration-300"
            style={{ width: `${((currentStep + 1) / TUTORIAL_STEPS.length) * 100}%` }}
          />
        </div>
      </div>
    </>
  );
};

export default OnboardingTutorial;

// Helper: Tutorial'ı manuel olarak tekrar göstermek için
export const resetTutorial = () => {
  localStorage.removeItem(STORAGE_KEY);
};

// Helper: Tutorial tamamlandı mı kontrol
export const isTutorialCompleted = () => {
  return localStorage.getItem(STORAGE_KEY) === 'true';
};
